// Forgot password, and setting or changing one from Account (F-IAM-02): a code to the email
// (`/email-otp/request-password-reset`), then the code and a new password (`/email-otp/reset-password`). The answer is
// the same whether or not the address has an account. Signed in, the page returns to where it came from (`back`).

import { useTranslation } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { Link, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../api/client';
import { useSession } from '../api/queries';
import { AuthLayout } from './AuthLayout';
import { useAuthFailure } from './auth-errors';
import { Failure, TextInput } from './form-controls';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = { readonly kind: 'email' } | { readonly kind: 'code'; readonly email: string } | { readonly kind: 'done' };

/** The reset flow's own wording for the better-auth codes it can tell apart; the rest read as the shared copy. */
const RESET_KEYS: Readonly<Record<string, string>> = {
  INVALID_OTP: 'forgot.failure.code',
  OTP_EXPIRED: 'forgot.failure.code',
  TOO_MANY_ATTEMPTS: 'forgot.failure.code',
  PASSWORD_COMPROMISED: 'forgot.failure.weak',
  PASSWORD_TOO_SHORT: 'forgot.failure.short',
};

export function ForgotPasswordPage(): ReactNode {
  const { t } = useTranslation('player');
  const search = useSearch({ from: '/forgot-password' });
  const signedIn = useSession().data != null;
  const [step, setStep] = useState<Step>({ kind: 'email' });

  return (
    <AuthLayout
      title={t('forgot.title')}
      subtitle={step.kind === 'email' ? t('forgot.intro') : undefined}
      footer={
        <Link to={signedIn ? (search.back ?? '/account') : '/sign-in'}>{signedIn ? t('forgot.backAccount') : t('forgot.back')}</Link>
      }
    >
      {step.kind === 'email' ? (
        <EmailStep initial={search.email ?? ''} onSent={(email) => setStep({ kind: 'code', email })} />
      ) : step.kind === 'code' ? (
        <CodeStep email={step.email} onDone={() => setStep({ kind: 'done' })} onChangeEmail={() => setStep({ kind: 'email' })} />
      ) : (
        <div className="pw-form">
          <p role="status">{signedIn ? t('forgot.doneSignedIn') : t('forgot.done')}</p>
          {signedIn ? (
            <Link to={search.back ?? '/account'} className="pw-btn pw-btn--primary">
              {t('nav.account')}
            </Link>
          ) : (
            <Link to="/sign-in" className="pw-btn pw-btn--primary">
              {t('forgot.signIn')}
            </Link>
          )}
        </div>
      )}
    </AuthLayout>
  );
}

function EmailStep({ initial, onSent }: { readonly initial: string; readonly onSent: (email: string) => void }): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<{ email: string }>({ defaultValues: { email: initial } });
  const submit = form.handleSubmit(async ({ email }) => {
    setFailure(null);
    try {
      await api.requestPasswordResetWithEmailOTP({ body: { email: email.trim() } });
      onSent(email.trim());
    } catch (error) {
      setFailure(failureOf(error, RESET_KEYS));
    }
  });
  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="email"
        label={t('forgot.email')}
        type="email"
        inputMode="email"
        autoComplete="email"
        autoFocus
        enterKeyHint="send"
        rules={{ required: t('signIn.required'), validate: (value: string) => EMAIL.test(value.trim()) || t('forgot.failure.email') }}
      />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('forgot.send')}
      </Button>
    </form>
  );
}

function CodeStep({ email, onDone, onChangeEmail }: { readonly email: string; readonly onDone: () => void; readonly onChangeEmail: () => void }): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const failureOf = useAuthFailure();
  const [failure, setFailure] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const form = useForm<{ code: string; password: string }>({ defaultValues: { code: '', password: '' } });
  const submit = form.handleSubmit(async ({ code, password }) => {
    setFailure(null);
    try {
      await api.resetPasswordWithEmailOTP({ body: { email, otp: code.trim(), password } });
      onDone();
    } catch (error) {
      setFailure(failureOf(error, RESET_KEYS));
    }
  });
  return (
    <form className="pw-form" onSubmit={(event) => void submit(event)} noValidate>
      <p role="status">{t('forgot.sentIntro', { email })}</p>
      <Failure message={failure} />
      <TextInput
        control={form.control}
        name="code"
        label={t('forgot.code')}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        maxLength={6}
        enterKeyHint="next"
        numeric
        rules={{ required: t('signIn.required') }}
      />
      <TextInput
        control={form.control}
        name="password"
        label={t('forgot.password')}
        description={t('forgot.passwordHint')}
        type="password"
        autoComplete="new-password"
        enterKeyHint="done"
        rules={{ required: t('signIn.required'), validate: (value: string) => value.length >= 8 || t('forgot.failure.short') }}
      />
      <Button type="submit" variant="primary" block busy={form.formState.isSubmitting}>
        {t('forgot.submit')}
      </Button>
      <div className="pw-form__actions">
        <Button
          variant="ghost"
          busy={resending}
          onClick={() => {
            setResending(true);
            void api
              .requestPasswordResetWithEmailOTP({ body: { email } })
              .catch(() => undefined)
              .finally(() => setResending(false));
          }}
        >
          {t('forgot.resend')}
        </Button>
        <Button variant="ghost" onClick={onChangeEmail}>
          {t('forgot.changeEmail')}
        </Button>
      </div>
    </form>
  );
}
